import { Component, effect, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { firstValueFrom, of, switchMap } from 'rxjs';

import { TranslatePipe } from '@core/i18n-legacy';
import { HeaderThreadComponent, HrAssistantChatComponent } from '@core/intelligence-smart-ui';
import { ButtonComponent, HeadingComponent } from '@core/user-interface-legacy';
import {
  canActivateDataSource,
  SimpleViewComponent,
  ViewsLocalStorageService,
} from '@core/views-legacy';
import { WrappedTableListGroupHeadingComponent } from '@core/views-ui';
import { dataSourceProviders, provideListLayout, provideViewBaseRoute } from '@core/views-util';
import { AskHrChatFeatureId } from '@tellent-ask-hr/core-types';
import { AskTemplatesDataService } from '@tellent-ask-hr/templates-logic';
import type { AskHrDynamicFormConfig } from '@tellent-ask-hr/templates-smart-ui';
import { TicketsDataService } from '@tellent-ask-hr/tickets-logic';
import type { CreateAskTicket } from '@tellent-ask-hr/tickets-types';

import { ChatUserTemplatesDataSourceService } from './chat-user-templates-data-source.service';
import { UserFormListItemComponent } from './chat-user-templates-list-item/chat-user-form-list-item.component';
import { CreateTicketComponent } from '../../create-ticket/create-ticket.component';

@Component({
  selector: 'tlc-chat-user-forms-view',
  template: `
    <tlc-chat-header-thread
      (back)="selectedTemplate() ? clearSelectedTemplate() : backToWelcome()"
      (close)="onClose()"
    />
    @if (selectedTemplate(); as template) {
      <div class="create-ticket">
        <tlc-create-ticket
          #createTicketComponent
          [template]="template"
          (ticketChanges)="ticketData.set($event)"
        />
        <div layout layout-space-between margin-top-medium margin-bottom>
          <rt-button variant="plain" (click)="clearSelectedTemplate()">
            {{ 'tlc.common.cancel' | translate }}
          </rt-button>
          <rt-button
            variant="primary"
            (click)="submit()"
            [disabled]="!createTicketComponent.isFormValid()"
          >
            {{ 'tlc.ask_hr.forms.chat.submit' | translate }}
          </rt-button>
        </div>
      </div>
    } @else {
      <div margin-lr-medium margin-top>
        <rt-heading variant="heading large">
          {{ 'tlc.ask_hr.forms.chat.select_type' | translate }}
        </rt-heading>
      </div>
      @if (canActivate()) {
        <rt-simple-view
          class="view"
          viewId="ask_templates:hr_assistant"
          [selfScrollEnabled]="true"
          [autoSetHeight]="false"
          [hasControls]="false"
        />
      }
    }
  `,
  imports: [
    SimpleViewComponent,
    HeadingComponent,
    CreateTicketComponent,
    ButtonComponent,
    HeaderThreadComponent,
    TranslatePipe,
  ],
  providers: [
    ViewsLocalStorageService,
    provideViewBaseRoute('/asks/templates'),
    ChatUserTemplatesDataSourceService,
    dataSourceProviders('ask_templates', {
      ask_templates: ChatUserTemplatesDataSourceService,
    }),
    provideListLayout({
      ask_templates: {
        header: WrappedTableListGroupHeadingComponent,
        record: UserFormListItemComponent,
        recordSettings: {
          hasPadding: false,
        },
      },
    }),
  ],
  styleUrls: ['./chat-user-templates-view.component.less'],
})
export class ChatUserTemplatesViewComponent {
  private readonly assistantChatComponent = inject(HrAssistantChatComponent);
  private readonly canActivateFn = canActivateDataSource();
  private readonly canActivate$ = this.canActivateFn();
  private readonly dataSourceService = inject(ChatUserTemplatesDataSourceService);
  private readonly templateDataService = inject(AskTemplatesDataService);
  private readonly ticketsDataService = inject(TicketsDataService);

  protected canActivate = toSignal(this.canActivate$);
  protected selectedTemplate = toSignal(
    toObservable(this.dataSourceService.selectedTicketId).pipe(
      switchMap(id => (id ? this.templateDataService.getTemplateById(id) : of(null))),
    ),
    { initialValue: null },
  );
  protected ticketData = signal<CreateAskTicket<AskHrDynamicFormConfig> | null>(null);

  private loadTemplate = effect(() => {
    const id = this.dataSourceService.selectedTicketId();
    if (!id) return;

    this.templateDataService.loadTemplateById(id).subscribe();
  });

  protected clearSelectedTemplate(): void {
    this.dataSourceService.selectedTicketId.set(null);
  }

  protected onClose() {
    this.assistantChatComponent.close.emit();
  }

  protected backToWelcome() {
    this.assistantChatComponent.clearFeature();
  }

  protected async submit() {
    const ticketData = this.ticketData();
    if (!ticketData) return;

    const result = await firstValueFrom(this.ticketsDataService.createTicket(ticketData));
    if (result.isSuccess) {
      this.assistantChatComponent.setFeatureById(AskHrChatFeatureId.Tickets);
    }
  }
}
